import type { App } from 'vue';
import { BaseService } from '../base';
import { BuriedCore, type BuriedCoreConfig, type BuriedData, type TrackingAdapter } from './core';

import { createAliTrackingAdapter } from './integrations/ali';
import { createYfxTrackingAdapter } from './integrations/yfx';

// 全局类型声明
declare global {
  interface Window {
    _yfx_trackdata?: Array<any>;
    _dgt?: Array<any>;
    dataLayer?: Array<any>;
  }
}

// 埋点服务配置接口
export interface BuriedServiceConfig extends BuriedCoreConfig {}

// 默认配置
const DEFAULT_CONFIG: BuriedServiceConfig = {
  autoTrack: true,
  enableClick: true,
  enableExposure: true,
  exposureThreshold: 0.5,
  customEvents: {},
  enableDevLog: true,
};

// 埋点服务实现
export class BuriedService extends BaseService {
  private config: BuriedServiceConfig;
  private tracker: BuriedCore;
  private isInstalled = false;
  private readonly trackingAdapters: TrackingAdapter[];

  constructor(config: BuriedServiceConfig = {}) {
    super('BuriedService');
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.trackingAdapters = [createAliTrackingAdapter(), createYfxTrackingAdapter()];
    this.tracker = new BuriedCore({
      config: this.config,
      send: data => this.sendBuriedData(data),
      trackingAdapters: this.trackingAdapters,
    });
  }

  // 初始化服务
  protected async onInitialize(app?: App): Promise<void> {
    // SSR 环境下不初始化埋点
    if (import.meta.server) return;

    this.tracker.init();

    if (app) {
      this.install(app);
    }
  }

  // 销毁服务
  protected async onDestroy(): Promise<void> {
    this.tracker.destroy();
    this.isInstalled = false;
    // @ts-expect-error - 清理引用
    this.tracker = null;
    // @ts-expect-error - 清理 config 引用
    this.config = null;
  }

  // 安装到 Vue 应用
  install(app: App): void {
    if (this.isInstalled) return;
    app.directive('buried', this.tracker.createDirective());
    this.isInstalled = true;
  }

  // 发送埋点数据
  private sendBuriedData(data: BuriedData): void {
    // SSR 环境下不执行
    if (import.meta.server || typeof window === 'undefined') return;

    if (window.dataLayer) {
      window.dataLayer.push({
        ...data,
      } as Record<string, unknown>);
    }
  }

  // 手动埋点
  public track(eventName: string, customData?: Record<string, unknown>): void {
    this.tracker.track(eventName, customData);
  }

  // 用户登录
  public login(userId: number | string): void {
    this.tracker.login(userId);
  }

  // 重新观察所有埋点元素
  public reobserveElements(): void {
    this.tracker.reobserveElements();
  }

  // 获取配置
  public getConfig(): BuriedServiceConfig {
    return this.tracker.getConfig();
  }

  // 更新配置
  public updateConfig(newConfig: Partial<BuriedServiceConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.tracker.updateConfig(this.config);
  }
}

// 创建埋点服务实例
export const createBuriedService = (config: BuriedServiceConfig = {}): BuriedService => {
  return new BuriedService(config);
};

// 全局埋点服务实例
let globalBuriedService: BuriedService | null = null;

// 初始化埋点服务
export const initBuriedService = async (
  config: BuriedServiceConfig = {},
): Promise<BuriedService> => {
  // SSR 环境下不初始化埋点服务（埋点服务仅在客户端使用）
  if (import.meta.server) {
    throw new Error('BuriedService should not be initialized in SSR environment');
  }

  if (!globalBuriedService) {
    globalBuriedService = createBuriedService(config);
    await globalBuriedService.initialize();
  } else if (Object.keys(config).length > 0) {
    // 如果服务已存在且有新配置，则更新配置
    globalBuriedService.updateConfig(config);
  }
  return globalBuriedService;
};

// 获取埋点服务实例
export const getBuriedService = () => {
  if (!globalBuriedService) {
    // 私有化不进行埋点
    return null;
  }
  return globalBuriedService;
};

// 便捷的埋点函数
export const trackBuried = (eventName: string, customData?: Record<string, unknown>): void => {
  if (import.meta.server) return;
  const service = getBuriedService();
  service?.track(eventName, customData);
};

// 重新观察埋点元素
export const reobserveBuried = (): void => {
  const service = getBuriedService();
  service?.reobserveElements();
};

// 便捷的登录埋点函数
export const loginBuried = (userId: number | string): void => {
  const service = getBuriedService();
  service?.login(userId);
};

export { BURIED_EVENT_TYPE, BuriedCore } from './core';
export type { BuriedCoreConfig, BuriedData, TrackingAdapter } from './core';

//
